import { Typography, ButtonPro, InputPro } from 'components';
import { useState } from 'react';
import './index.scss';

interface ExportColumn {
  id: string;
  label: string;
  checked: boolean;
}

const columns: ExportColumn[] = [
  { id: 'EmployeeFullName', label: 'Employee Full Name', checked: true },
  {
    id: 'EmployeeNameWithInitials',
    label: 'Name with Initials',
    checked: true
  },
  { id: 'EmployeeNickName', label: 'Nick Name', checked: false },
  { id: 'Upn', label: 'UPN/Username', checked: true },
  { id: 'EpfNumber', label: 'EPF Number', checked: false },
  { id: 'MobileNumber', label: 'Mobile Number', checked: true },
  { id: 'PersonalEmail', label: 'Personal Email', checked: true },
  { id: 'NIC', label: 'NIC Number', checked: false },
  { id: 'PassportNumber', label: 'Passport Number', checked: false },
  { id: 'DateOfBirth', label: 'Date of Birth', checked: false },
  { id: 'Gender', label: 'Gender', checked: false },
  { id: 'MaritalStatus', label: 'Marital Status', checked: false },
  { id: 'Religion', label: 'Religion', checked: false },
  { id: 'Nationality', label: 'Nationality', checked: false },
  { id: 'HouseNumber', label: 'House Number', checked: false },
  { id: 'Address', label: 'Address', checked: false },
  { id: 'District', label: 'District', checked: false },
  { id: 'Province', label: 'Province', checked: false },
  { id: 'TemporyAddress', label: 'Temporary Address', checked: false },
  {
    id: 'ResidentialPhoneNumber',
    label: 'Residential Phone',
    checked: false
  },
  {
    id: 'EmergencyContactPerson',
    label: 'Emergency Contact Person',
    checked: false
  },
  {
    id: 'EmergencyContactPersonPhoneNumber',
    label: 'Emergency Contact Phone',
    checked: false
  },
  {
    id: 'EmergencyContactPersonRelationship',
    label: 'Emergency Contact Relationship',
    checked: false
  }
];

export const EmployeeSummaryFilter = () => {
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [exportColumns, setExportColumns] = useState<ExportColumn[]>(columns);
  const [isExporting, setIsExporting] = useState(false);

  const handleColumnToggle = (columnId: string) => {
    setExportColumns((prev) =>
      prev.map((col) =>
        col.id === columnId ? { ...col, checked: !col.checked } : col
      )
    );
  };

  const handleSelectAll = () => {
    const allSelected = exportColumns.every((col) => col.checked);
    setExportColumns((prev) =>
      prev.map((col) => ({ ...col, checked: !allSelected }))
    );
  };

  const handleExportData = async () => {
    const selectedColumns = exportColumns.filter((col) => col.checked);

    if (selectedColumns.length === 0) {
      alert('Please select at least one column to export');
      return;
    }

    setIsExporting(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/employees/filter-export-xlsx`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'ngrok-skip-browser-warning': 'true'
          },
          body: JSON.stringify({
            PageNumber: 1,
            PageSize: 1000,
            Filters: { IsActive: 'true' },
            Fields: selectedColumns.map((col) => col.id)
          })
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `employee-summary-${
          new Date().toISOString().split('T')[0]
        }.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        setIsExportDialogOpen(false);
      } else {
        throw new Error('Export failed');
      }
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export data. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <div className="employee-summary-filter-container">
        <div className="employee-summary-title">
          <Typography
            text="Employee Summary"
            type="base_regular"
            weight="bold"
          />
        </div>
        <div className="employee-summary-filter">
          <InputPro
            label=""
            name="searchEmployees"
            placeholder="Search employees..."
            value={searchTerm}
            onChange={setSearchTerm}
            inputWrapperClass="search-input-wrapper"
          />
          <ButtonPro
            label="Filter"
            type="white"
            size="base"
            htmlType="button"
          />
          <ButtonPro
            label="Export"
            type="primary"
            size="base"
            htmlType="button"
            onClick={() => setIsExportDialogOpen(true)}
            buttonClassNames="export-btn"
          />
        </div>
      </div>

      {/* Export Dialog */}
      {isExportDialogOpen && (
        <div className="export-dialog-overlay">
          <div className="export-dialog">
            <div className="export-dialog-header">
              <Typography
                text="Export Employee Data"
                type="base_regular"
                weight="bold"
              />
              <ButtonPro
                label="×"
                type="icon"
                size="small"
                onClick={() => setIsExportDialogOpen(false)}
                buttonClassNames="close-btn"
              />
            </div>

            <div className="export-dialog-content">
              <div className="column-selection-header">
                <Typography
                  text="Select columns to export:"
                  type="small_regular"
                  weight="bold"
                />
                <ButtonPro
                  label={
                    exportColumns.every((col) => col.checked)
                      ? 'Deselect All'
                      : 'Select All'
                  }
                  type="text"
                  size="small"
                  onClick={handleSelectAll}
                  htmlType="button"
                  buttonClassNames="select-all-btn"
                />
              </div>

              <div className="column-list">
                {exportColumns.map((column) => (
                  <div key={column.id} className="column-item">
                    <label className="column-checkbox">
                      <input
                        type="checkbox"
                        checked={column.checked}
                        onChange={() => handleColumnToggle(column.id)}
                      />
                      <span className="checkmark"></span>
                      <span className="column-label">{column.label}</span>
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="export-dialog-footer">
              <ButtonPro
                label="Cancel"
                type="white"
                size="base"
                onClick={() => setIsExportDialogOpen(false)}
                disabled={isExporting}
                buttonClassNames="cancel-btn"
              />
              <ButtonPro
                label={isExporting ? 'Exporting...' : 'Export Data'}
                type="primary"
                size="base"
                onClick={handleExportData}
                disabled={
                  isExporting ||
                  exportColumns.filter((col) => col.checked).length === 0
                }
                buttonClassNames="export-data-btn"
                isLoading={isExporting}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};
